import { computed } from 'vue';
import { useQuery } from '@vue/apollo-composable';
import { FETCH_SEEKER_DASHBOARD_DATA_QUERY, FETCH_ADVENTURE_DATA } from '@/graphql/account/queries';
import { BH_RESERVATION_REQUESTS } from '@/graphql/backup_care/queries';
import { processApolloError } from '@/helpers/bookings/bookingsDataHelper';
import type { Ref } from 'vue';
import type { Account } from '@/models/account';

export type DashboardJobEdge = {
  node: {
    id: string;
    isActive: boolean;
    status: string;
    [key: string]: any;
  };
};

export type DashboardViewer = {
  photoUrl?: string | null;
  jobs: { totalCount: number; edges: DashboardJobEdge[] };
  [key: string]: any;
};

type DashboardQuery = {
  sitterSearch: { edges: any[] };
  viewer: DashboardViewer | null;
};

type AdventureQuery = {
  adventureSearch: { edges: any[] } | null;
};

type ReservationRequest = {
  scCareSessions?: unknown[] | null;
  [key: string]: any;
};

type ReservationRequestsQuery = {
  corp: { backupCare: { bhReservationRequests: ReservationRequest[] | null } | null } | null;
};

export function useDashboardData(account: Readonly<Ref<Account>>) {
  const isCorporate = computed(() => account.value.isCorporate());

  const {
    result: dashboardResult,
    loading: dashboardLoading,
    error: dashboardError,
    onError: onDashboardError,
  } = useQuery<DashboardQuery>(FETCH_SEEKER_DASHBOARD_DATA_QUERY, () => ({
    geoPointX: account.value.location.longitude.toString(),
    geoPointY: account.value.location.latitude.toString(),
  }));

  // A failed adventure search hides the adventures section and leaves the rest of the dashboard up.
  const { result: adventureResult, onError: onAdventureError } = useQuery<AdventureQuery>(FETCH_ADVENTURE_DATA, () => ({
    longitude: account.value.location.longitude.toString(),
    latitude: account.value.location.latitude.toString(),
  }));

  // errorPolicy 'all' keeps reservation requests whose sitter account is locked.
  const {
    result: corpResult,
    loading: corpLoading,
    onError: onCorpError,
  } = useQuery<ReservationRequestsQuery>(BH_RESERVATION_REQUESTS, null, () => ({
    errorPolicy: 'all',
    enabled: isCorporate.value,
  }));

  const dashboardData = computed(() => dashboardResult.value?.viewer ?? null);
  const sitters = computed(() => dashboardResult.value?.sitterSearch?.edges ?? []);
  const adventures = computed(() => adventureResult.value?.adventureSearch?.edges ?? []);
  const reservationRequests = computed(() =>
    (corpResult.value?.corp?.backupCare?.bhReservationRequests ?? []).filter((request) => request.scCareSessions?.length)
  );

  const isLoading = computed(() => dashboardLoading.value && !dashboardResult.value);
  const hasError = computed(() => !isLoading.value && (!!dashboardError.value || !dashboardData.value));
  const outOfNetworkLoading = computed(() => isCorporate.value && corpLoading.value && !corpResult.value);

  onDashboardError((error) => console.error(error));
  onAdventureError((error) => console.error(error));
  onCorpError((error) => processApolloError(error, 'bhReservationRequests'));

  return { dashboardData, sitters, adventures, reservationRequests, isLoading, hasError, outOfNetworkLoading };
}
